<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';

import MetricCard from './overview/MetricCard.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatusTimeline = useMapGetter('getAgentStatusTimeline');
const agents = useMapGetter('agents/getAgents');
const agentSessions = useMapGetter('getAgentOnlineSessions');

const HOUR_MARKS = Array.from({ length: 9 }, (_, i) => i * 3);
const DAY_SECONDS = 86400;
const dayStart = startOfDay(new Date());
const dayStartUnix = getUnixTime(dayStart);

const fetchData = () => {
  store.dispatch('fetchAgentStatusTimeline', {
    from: dayStartUnix,
    to: getUnixTime(endOfDay(dayStart)),
  });
};

const { startRefetching } = useLiveRefresh(fetchData);

const segmentClass = name => (name === 'busy' ? 'bg-n-amber-9' : 'bg-n-teal-9');

const sessionBar = session => {
  const startOffset = Math.max(session.started_at - dayStartUnix, 0);
  const endOffset = Math.min(session.ended_at - dayStartUnix, DAY_SECONDS);
  const statusLabel = t(
    `OVERVIEW_REPORTS.AGENT_STATUS.${session.name.toUpperCase()}`
  );
  return {
    key: `${session.started_at}-${session.ended_at}`,
    left: (startOffset / DAY_SECONDS) * 100,
    width: Math.max(((endOffset - startOffset) / DAY_SECONDS) * 100, 0.3),
    class: segmentClass(session.name),
    tooltip: `${statusLabel} ${format(new Date(session.started_at * 1000), 'HH:mm')} - ${format(new Date(session.ended_at * 1000), 'HH:mm')}`,
  };
};

const agentRows = computed(() =>
  agents.value.map(agent => ({
    id: agent.id,
    name: agent.available_name || agent.name,
    thumbnail: agent.thumbnail,
    status: agent.availability_status,
    bars: agentStatusTimeline.value
      .filter(session => session.user_id === agent.id)
      .map(sessionBar),
  }))
);

// Drill-down: clicking a row expands a single-agent, any-day online-session view below it,
// reusing the same agent_online_sessions endpoint the old standalone timeline used.
const expandedAgentId = ref(null);
const expandedDate = ref(format(new Date(), 'yyyy-MM-dd'));

const isFetchingAgentSessions = computed(
  () => uiFlags.value.isFetchingAgentOnlineSessions
);

const expandedDayStartUnix = computed(() =>
  getUnixTime(startOfDay(new Date(`${expandedDate.value}T00:00:00`)))
);

const fetchExpandedSessions = () => {
  if (!expandedAgentId.value) return;
  const day = new Date(`${expandedDate.value}T00:00:00`);
  store.dispatch('fetchAgentOnlineSessions', {
    from: getUnixTime(startOfDay(day)),
    to: getUnixTime(endOfDay(day)),
    agentId: expandedAgentId.value,
  });
};

const toggleAgentExpanded = agentId => {
  expandedAgentId.value = expandedAgentId.value === agentId ? null : agentId;
  if (expandedAgentId.value) fetchExpandedSessions();
};

watch(expandedDate, fetchExpandedSessions);

const expandedSessionBars = computed(() =>
  agentSessions.value.map(session => {
    const startOffset = Math.max(
      session.started_at - expandedDayStartUnix.value,
      0
    );
    const endOffset = Math.min(
      session.ended_at - expandedDayStartUnix.value,
      DAY_SECONDS
    );
    return {
      key: `${session.started_at}-${session.ended_at}`,
      left: (startOffset / DAY_SECONDS) * 100,
      width: Math.max(((endOffset - startOffset) / DAY_SECONDS) * 100, 0.3),
      tooltip: `${format(new Date(session.started_at * 1000), 'HH:mm')} - ${format(new Date(session.ended_at * 1000), 'HH:mm')}`,
    };
  })
);

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-row flex-wrap max-w-full">
    <MetricCard
      :header="t('OVERVIEW_REPORTS.TEAM_SESSIONS_TIMELINE.HEADER')"
      :is-loading="uiFlags.isFetchingAgentStatusTimeline"
    >
      <div
        v-if="agentRows.length"
        class="w-full min-w-[700px] flex flex-col gap-3"
      >
        <template v-for="row in agentRows" :key="row.id">
          <button
            type="button"
            class="grid grid-cols-[13rem_1fr] items-center gap-3 rounded-md hover:bg-n-alpha-1 -mx-1 px-1 py-0.5 text-left"
            @click="toggleAgentExpanded(row.id)"
          >
            <div class="flex items-center gap-2 min-w-0">
              <Avatar
                :src="row.thumbnail"
                :name="row.name"
                :status="row.status"
                :size="24"
                hide-offline-status
                rounded-full
              />
              <span class="text-sm text-n-slate-12 truncate">{{
                row.name
              }}</span>
            </div>
            <div
              class="relative h-6 w-full rounded-md bg-n-slate-2 dark:bg-n-slate-1/30 overflow-hidden"
            >
              <div
                v-for="bar in row.bars"
                :key="bar.key"
                v-tooltip="bar.tooltip"
                class="absolute top-0 h-full cursor-pointer"
                :class="bar.class"
                :style="{ left: `${bar.left}%`, width: `${bar.width}%` }"
              />
            </div>
          </button>
          <div
            v-if="expandedAgentId === row.id"
            class="grid grid-cols-[13rem_1fr] gap-3 -mt-1 mb-1"
          >
            <div
              class="flex items-center text-xs text-n-slate-11 justify-self-end"
            >
              {{ t('OVERVIEW_REPORTS.AGENT_SESSIONS_TIMELINE.HEADER') }}
            </div>
            <div class="flex flex-col gap-1.5">
              <input
                v-model="expandedDate"
                type="date"
                class="h-7 w-40 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
              />
              <div
                v-if="isFetchingAgentSessions"
                class="text-n-slate-11 text-xs py-2"
              >
                {{ t('OVERVIEW_REPORTS.AGENT_SESSIONS_TIMELINE.LOADING') }}
              </div>
              <div
                v-else-if="!expandedSessionBars.length"
                class="text-n-slate-11 text-xs py-2"
              >
                {{ t('OVERVIEW_REPORTS.AGENT_SESSIONS_TIMELINE.NO_SESSIONS') }}
              </div>
              <div
                v-else
                class="relative h-5 w-full rounded-md bg-n-slate-2 dark:bg-n-slate-1/30 overflow-hidden"
              >
                <div
                  v-for="bar in expandedSessionBars"
                  :key="bar.key"
                  v-tooltip="bar.tooltip"
                  class="absolute top-0 h-full bg-n-teal-9 dark:bg-n-teal-8 cursor-pointer"
                  :style="{ left: `${bar.left}%`, width: `${bar.width}%` }"
                />
              </div>
            </div>
          </div>
        </template>
        <div class="grid grid-cols-[13rem_1fr] gap-3">
          <span />
          <div class="flex justify-between text-[10px] text-n-slate-11">
            <span v-for="mark in HOUR_MARKS" :key="mark">{{
              t('OVERVIEW_REPORTS.TEAM_SESSIONS_TIMELINE.HOUR_MARK', {
                hour: mark,
              })
            }}</span>
          </div>
        </div>
        <div class="flex gap-4 text-xs text-n-slate-11">
          <span class="flex items-center gap-1">
            <i class="size-1.5 rounded-full bg-n-teal-9" />
            {{ t('OVERVIEW_REPORTS.AGENT_STATUS.ONLINE') }}
          </span>
          <span class="flex items-center gap-1">
            <i class="size-1.5 rounded-full bg-n-amber-9" />
            {{ t('OVERVIEW_REPORTS.AGENT_STATUS.BUSY') }}
          </span>
          <span class="flex items-center gap-1">
            <i class="size-1.5 rounded-full bg-n-slate-4" />
            {{ t('OVERVIEW_REPORTS.TEAM_SESSIONS_TIMELINE.OFFLINE') }}
          </span>
        </div>
      </div>
      <EmptyState
        v-else
        :title="t('OVERVIEW_REPORTS.TEAM_SESSIONS_TIMELINE.NO_AGENTS')"
      />
    </MetricCard>
  </div>
</template>
